import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { ceil, floor, range } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  BORDER_2,
  FG_1,
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_WEIGHT_SEMIBOLD,
  HOVER_GREY,
  PRIMARY_COLOR,
  PRIMARY_HOVER,
  RADIUS_MD,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';

// Keep ≥ 5 so we can fit: first + ellipsis + current ± n + ellipsis + last.
const MAX_PAGES_FOR_PAGING = 7;
const ELLIPSIS = '…';

/**
 * BetaPagination — beta-styled pagination matching the design mockup.
 *
 * Mirrors the page-tile logic of the shared `PaginationBar` (so the windowing
 * with ellipses behaves the same), but with the rounded purple "Prev / 1 2 3 …
 * Next" look from the mockup instead of react-bootstrap's `<Pagination>`.
 *
 * Props:
 *   totalPages         total number of pages
 *   currentPageNumber  zero-indexed current page
 *   onPageChange(n)    invoked with zero-indexed page
 */
function BetaPagination({ totalPages, currentPageNumber, onPageChange }) {
  const pageTiles = useMemo(() => {
    if (totalPages <= MAX_PAGES_FOR_PAGING) return range(totalPages);
    const lastPageIndex = totalPages - 1;
    const numberOfMidTiles = MAX_PAGES_FOR_PAGING - 2;
    const lowerDisplayIndex = numberOfMidTiles - 1;
    const upperDisplayIndex = lastPageIndex - (numberOfMidTiles - 1);

    if (currentPageNumber < lowerDisplayIndex) {
      return [...range(numberOfMidTiles), ELLIPSIS, lastPageIndex];
    }
    if (currentPageNumber > upperDisplayIndex) {
      return [0, ELLIPSIS, ...range(totalPages - numberOfMidTiles, totalPages)];
    }
    const offset = (numberOfMidTiles - 3) / 2;
    const leftOffset = floor(offset);
    const rightOffset = ceil(offset);
    return [
      0,
      ELLIPSIS,
      ...range(currentPageNumber - leftOffset, currentPageNumber + rightOffset + 1),
      ELLIPSIS,
      lastPageIndex,
    ];
  }, [currentPageNumber, totalPages]);

  if (totalPages <= 1) return null;

  const isFirst = currentPageNumber === 0;
  const isLast = currentPageNumber >= totalPages - 1;

  const goPrev = () => onPageChange(Math.max(currentPageNumber - 1, 0));
  const goNext = () => onPageChange(Math.min(currentPageNumber + 1, totalPages - 1));

  return (
    <nav className={css(styles.bar)} aria-label="pagination">
      <button
        type="button"
        className={css(styles.navButton, isFirst && styles.disabled)}
        onClick={goPrev}
        disabled={isFirst}
      >
        <FontAwesomeIcon icon={faAngleLeft} className={css(styles.chevron)} />
        Prev
      </button>

      {pageTiles.map((tile, idx) => {
        if (tile === ELLIPSIS) {
          // eslint-disable-next-line react/no-array-index-key
          return (
            <span key={`e-${idx}`} className={css(styles.ellipsis)}>
              {ELLIPSIS}
            </span>
          );
        }
        const isActive = tile === currentPageNumber;
        return (
          <button
            key={tile}
            type="button"
            className={css(styles.tile, isActive && styles.tileActive)}
            onClick={() => onPageChange(tile)}
            aria-current={isActive ? 'page' : undefined}
          >
            {tile + 1}
          </button>
        );
      })}

      <button
        type="button"
        className={css(styles.navButton, isLast && styles.disabled)}
        onClick={goNext}
        disabled={isLast}
      >
        Next
        <FontAwesomeIcon icon={faAngleRight} className={css(styles.chevron)} />
      </button>
    </nav>
  );
}

const TILE_SIZE = 36;

const styles = StyleSheet.create({
  bar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '12px 0 4px',
    flexWrap: 'wrap',
  },

  tile: {
    minWidth: TILE_SIZE,
    height: TILE_SIZE,
    padding: '0 10px',
    borderRadius: RADIUS_MD,
    border: `1px solid ${BORDER_2}`,
    backgroundColor: WHITE,
    color: FG_1,
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: FONT_SIZE_SMALL,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.12s, border-color 0.12s, color 0.12s',
    ':hover': {
      backgroundColor: HOVER_GREY,
      borderColor: BORDER_2,
    },
    outline: 'none',
    ':focus-visible': {
      boxShadow: `0 0 0 2px ${PRIMARY_COLOR}`,
    },
  },

  tileActive: {
    backgroundColor: PRIMARY_COLOR,
    borderColor: PRIMARY_COLOR,
    color: WHITE,
    ':hover': {
      backgroundColor: PRIMARY_HOVER,
      borderColor: PRIMARY_HOVER,
    },
  },

  navButton: {
    height: TILE_SIZE,
    padding: '0 14px',
    borderRadius: RADIUS_MD,
    border: `1px solid ${BORDER_2}`,
    backgroundColor: WHITE,
    color: FG_1,
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: FONT_SIZE_SMALL,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'background-color 0.12s, border-color 0.12s, color 0.12s',
    ':hover': {
      backgroundColor: HOVER_GREY,
    },
    outline: 'none',
    ':focus-visible': {
      boxShadow: `0 0 0 2px ${PRIMARY_COLOR}`,
    },
  },

  disabled: {
    color: FG_3,
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: WHITE,
    },
  },

  chevron: {
    fontSize: 11,
    opacity: 0.7,
  },

  ellipsis: {
    minWidth: TILE_SIZE,
    height: TILE_SIZE,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: FG_3,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_SMALL,
    userSelect: 'none',
  },
});

BetaPagination.propTypes = {
  totalPages: PropTypes.number.isRequired,
  currentPageNumber: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

export default BetaPagination;
